import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Tags } from "lucide-react";
import { KindSelect } from "@/components/KindSelect";

// Proposes a kind for every untagged line item / assembly from its name; the owner reviews, adjusts, applies.
export function AutoTagKindsDialog({ open, onOpenChange, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setData(null);
    api.post("/catalog/kinds/auto", { apply: false })
      .then((r) => { setData(r.data); setRows((r.data.proposals || []).map((p) => ({ ...p, on: true }))); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not scan the catalog"))
      .finally(() => setLoading(false));
  }, [open]);

  const apply = async () => {
    const items = rows.filter((r) => r.on && r.kind).map((r) => ({ catalog_id: r.catalog_id, kind: r.kind }));
    if (!items.length) { onOpenChange(false); return; }
    setApplying(true);
    try {
      const r = await api.post("/catalog/kinds/auto", { apply: true, items });
      toast.success(`Tagged ${r.data.applied} catalog item${r.data.applied === 1 ? "" : "s"}`);
      onApplied?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply the tags"); }
    finally { setApplying(false); }
  };

  const selected = rows.filter((r) => r.on && r.kind).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="auto-tag-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tags className="w-4 h-4" /> Auto-tag kinds</DialogTitle>
          <DialogDescription>Each untagged line item and assembly gets a kind from its name (pile, tracker row, module, inverter, conductor…). The proposal's Scope and Quantities prints only tagged lines, so new bids built from these items fill in on their own. Adjust any guess before applying.</DialogDescription>
        </DialogHeader>
        {loading && <div className="py-8 flex items-center justify-center text-sm text-slate-500"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading the catalog…</div>}
        {!loading && data && (
          <>
            <div className="text-xs text-slate-500" data-testid="auto-tag-summary">
              {data.scanned} untagged item{data.scanned === 1 ? "" : "s"} scanned · {rows.length} with a match{data.no_match ? ` · ${data.no_match} with no match (tag those by hand)` : ""}
            </div>
            {rows.length > 0 && (
              <div className="max-h-[50vh] overflow-auto border border-slate-100 rounded-lg divide-y divide-slate-100" data-testid="auto-tag-rows">
                {rows.map((r, i) => (
                  <div key={r.catalog_id} className="grid grid-cols-[24px_1fr_180px] gap-2 items-center px-2 py-1.5">
                    <input type="checkbox" checked={r.on} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))} data-testid={`auto-tag-on-${i}`} />
                    <div className="min-w-0"><div className="text-sm truncate">{r.name}</div><div className="text-[10px] text-slate-400 uppercase">{r.type === "assembly" ? "Assembly" : "Line item"}{r.category ? ` · ${r.category}` : ""}</div></div>
                    <KindSelect value={r.kind} onChange={(v) => setRows(rows.map((x, j) => (j === i ? { ...x, kind: v } : x)))} testid={`auto-tag-kind-${i}`} />
                  </div>
                ))}
              </div>
            )}
            {rows.length === 0 && <div className="py-6 text-center text-sm text-slate-500" data-testid="auto-tag-empty">Nothing to tag: every item that matches a kind already carries one.</div>}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="auto-tag-cancel">Cancel</Button>
          <Button onClick={apply} disabled={applying || loading || !selected} className="bg-[#003E87] hover:bg-[#00306a]" data-testid="auto-tag-apply">
            {applying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Tags className="w-4 h-4 mr-1.5" />} Tag {selected} item{selected === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
